import { Loader } from "@/components/shared";
import { client } from "@/lib/appwrite/config";
import { useGetCurrentUser, useGetRelatedUsers } from "@/lib/react-query/queries";
import { IUser } from "@/types";
import { useEffect, memo } from "react";

// Add this constant
const BUSICONN_AGENT_ID = "68575f4e000617f8b3f6"; 

interface ChatListProps {
  onSelectUser: (user: IUser) => void;
  lastMessages: { 
    [userId: string]: {  // Changed to string keys
      message: string;
      timestamp: Date;
      isRead?: boolean;
    } 
  };
}

const UserItem = memo(({ 
  user, 
  lastMessage, 
  onSelectUser
}: {
  user: IUser & { isConversation?: boolean, participants?: IUser[] },
  lastMessage?: {
    message: string;
    timestamp: Date;
    isRead?: boolean;
  },
  onSelectUser: (user: IUser) => void
}) => {
  const lastMessageText = lastMessage?.message || "No messages yet";
  const isUnread = lastMessage?.isRead === false;
  
  return (
    <div
      onClick={() => onSelectUser(user)}
      className={`chat-list-item flex items-center p-4 rounded-lg hover:bg-gray-600 cursor-pointer transition-colors text-white ${
        isUnread ? 'bg-gray-700' : 'bg-gray-800'
      }`}
    >
      {/* Special avatar for conversation pairs */}
      {user.isConversation && user.participants ? (
        <div className="flex -space-x-2 mr-3">
          <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-medium border-2 border-gray-800">
            {user.participants[0]?.name?.charAt(0).toUpperCase()}
          </div>
          <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center text-white text-sm font-medium border-2 border-gray-800">
            {user.participants[1]?.name?.charAt(0).toUpperCase()}
          </div>
        </div>
      ) : user.imageUrl ? (
        <img 
          src={user.imageUrl} 
          alt={user.name || 'User'} 
          className="w-10 h-10 rounded-full mr-3 object-cover"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.src = '/fallback-avatar.png';
          }}
          loading="lazy"
        />
      ) : (
        <div className="w-10 h-10 rounded-full mr-3 bg-gray-500 flex items-center justify-center">
          <span className="text-white text-lg">
            {(user.name || 'U').charAt(0).toUpperCase()}
          </span>
        </div>
      )}
      
      <div className="flex-grow overflow-hidden">
        <div className="flex justify-between items-center">
          <p className={`font-semibold ${isUnread ? 'text-white' : 'text-gray-300'} ${
            user.isConversation ? 'text-sm' : ''
          }`}>
            {user.name || 'Unknown User'}
          </p>
          {lastMessage?.timestamp && (
            <span className="text-xs text-gray-400">
              {lastMessage.timestamp.toLocaleTimeString([], { 
                hour: '2-digit', 
                minute: '2-digit' 
              })}
            </span>
          )}
        </div>
        <p className={`text-sm truncate max-w-[200px] ${
          isUnread ? 'text-gray-200' : 'text-gray-400'
        }`}>
          {lastMessageText}
        </p>
      </div>
      {isUnread && (
        <div className="ml-2 w-2 h-2 rounded-full bg-blue-500"></div>
      )}
    </div>
  );
});

const ChatList: React.FC<ChatListProps> = ({ onSelectUser, lastMessages }) => {
  const { data: currentUser } = useGetCurrentUser();
  const isAgent = currentUser?.$id === BUSICONN_AGENT_ID;
  
  const { data: users, isLoading, refetch } = useGetRelatedUsers(
    currentUser?.$id || "", 
    isAgent
  );
  
  // Real-time updates for new messages
  useEffect(() => {
    if (!currentUser) return;

    const unsubscribe = client.subscribe(
      [`databases.${import.meta.env.VITE_APPWRITE_DATABASE_ID}.collections.${
        import.meta.env.VITE_APPWRITE_MESSAGE_COLLECTION_ID
      }.documents`],
      (response) => {
        if (response.events.includes(`databases.${import.meta.env.VITE_APPWRITE_DATABASE_ID}.collections.${
          import.meta.env.VITE_APPWRITE_MESSAGE_COLLECTION_ID
        }.documents.*.create`)) {
          refetch();
        }
      }
    );

    return () => unsubscribe();
  }, [currentUser, refetch]);

  if (isLoading) return <Loader />;

  return (
    <div className="chat-list w-full bg-gray-800 p-4 border-b border-gray-700">
      <h2 className="text-lg font-semibold mb-4 text-white">
        {isAgent ? "Active Conversations" : "Chats"}
      </h2>
      <div className="space-y-2">
        {users?.map((user) => (
          <UserItem
            key={user.$id}
            user={{
              id: user.$id,
              name: user.name,
              username: user.username,
              emailOrPhone: user.emailOrPhone,
              imageUrl: user.imageUrl,
              bio: user.bio,
              acceptedSLA: user.acceptedSLA,
              isConversation: user.isConversation,
              participants: user.participants
            }}
            lastMessage={lastMessages[user.$id]}
            onSelectUser={onSelectUser}
          />
        ))}
      </div>
    </div>
  );
};

export default ChatList;